import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { CampClase } from '@/domains/lessons/features/completion/components/CampClase';
import { MINIM_PARTICIPANTI, numarEleviValid, numarParticipantiValid } from '../hooks/use-collab-submission';

interface Valori { submittedByName: string; description: string; clase: string; numarElevi: string }

interface Props extends Valori {
    /** Activitatea practica cere clasele (punctajul se da pe clase) si, optional, numarul de elevi. */
    cereClase: boolean;
    fragmenteRespinse: string[];
    onChange: (v: Valori) => void;
    canContinue: boolean; onContinue: () => void;
}

const EROARE_CLASE_ID = 'colab-clase-eroare';
const EROARE_ELEVI_ID = 'colab-elevi-eroare';
const EROARE_PARTICIPANTI_ID = 'colab-participanti-eroare';

/**
 * Pasul 3: cine trimite si, pe activitatea practica, cu ce clase - aceleasi campuri ca la profesor
 * (`CampClase`). La exercitiul cu clasa clasele nu se cer (fisele le numara echipa ECOTIC la
 * aprobare), dar numarul de elevi da — obligatoriu, minim 2 (sesizarea ECOTIC din 2026-10-26).
 */
export function StepDetails({ submittedByName, description, clase, numarElevi, cereClase, fragmenteRespinse, onChange, canContinue, onContinue }: Props) {
    const valori: Valori = { submittedByName, description, clase, numarElevi };
    const [claseAtinse, setClaseAtinse] = useState(false);
    const claseLipsa = cereClase && claseAtinse && clase.trim() === '';
    const eleviGresit = cereClase && !numarEleviValid(numarElevi);
    // Exercitiul cu clasa: numarul e obligatoriu; eroarea apare dupa ce omul a trecut prin camp.
    const [participantiAtinsi, setParticipantiAtinsi] = useState(false);
    const participantiGresit = !cereClase && participantiAtinsi && !numarParticipantiValid(numarElevi);

    // Serverul n-a inteles clasele (400): omul a fost adus inapoi aici de la pasul de fisiere.
    useEffect(() => {
        if (!fragmenteRespinse.length) return;
        const camp = document.getElementById('camp-clase');
        camp?.focus({ preventScroll: true });
        camp?.scrollIntoView({ block: 'center' });
    }, [fragmenteRespinse]);

    return (
        <form className="flex flex-col gap-5" noValidate onSubmit={(e) => { e.preventDefault(); if (canContinue) onContinue(); }}>
            <label className="flex flex-col gap-1">
                <span className="font-medium">Numele tău *</span>
                <Input value={submittedByName} maxLength={120} autoComplete="name" className="min-h-12 text-base"
                       onChange={(e) => onChange({ ...valori, submittedByName: e.target.value })} />
            </label>

            {cereClase ? (
                <>
                    {/* onBlur urca din Input: „obligatoriu" apare dupa ce omul a trecut prin camp. */}
                    <div className="space-y-2" onBlur={() => setClaseAtinse(true)} data-colab-clase>
                        <CampClase
                            value={clase}
                            onChange={(v) => onChange({ ...valori, clase: v })}
                            fragmenteRespinse={fragmenteRespinse}
                            label="Cu ce clase ați făcut activitatea? *"
                            errorId={claseLipsa ? EROARE_CLASE_ID : undefined}
                        />
                        {claseLipsa && <p id={EROARE_CLASE_ID} className="text-sm text-error">Scrie cel puțin o clasă.</p>}
                    </div>
                    <div className="flex flex-col gap-1">
                        <label htmlFor="colab-elevi" className="font-medium">Câți elevi au participat? (opțional)</label>
                        <Input
                            id="colab-elevi"
                            value={numarElevi}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength={6}
                            autoComplete="off"
                            className="min-h-12 text-base sm:max-w-40"
                            aria-invalid={eleviGresit || undefined}
                            aria-describedby={eleviGresit ? EROARE_ELEVI_ID : undefined}
                            onChange={(e) => onChange({ ...valori, numarElevi: e.target.value })}
                        />
                        {eleviGresit && <p id={EROARE_ELEVI_ID} className="text-sm text-error">Scrie un număr, de exemplu 24.</p>}
                    </div>
                </>
            ) : (
                <>
                    <div className="flex flex-col gap-1" data-colab-participanti>
                        <label htmlFor="colab-participanti" className="font-medium">
                            Câți elevi au participat? <span aria-hidden="true" className="text-error">*</span>
                            <span className="sr-only">(obligatoriu)</span>
                        </label>
                        <Input
                            id="colab-participanti"
                            value={numarElevi}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength={6}
                            autoComplete="off"
                            required
                            className="min-h-12 text-base sm:max-w-40"
                            aria-invalid={participantiGresit || undefined}
                            aria-describedby={participantiGresit ? EROARE_PARTICIPANTI_ID : undefined}
                            onBlur={() => setParticipantiAtinsi(true)}
                            onChange={(e) => onChange({ ...valori, numarElevi: e.target.value })}
                        />
                        {participantiGresit && (
                            <p id={EROARE_PARTICIPANTI_ID} className="text-sm text-error">
                                Scrie câți elevi au participat (minim {MINIM_PARTICIPANTI}).
                            </p>
                        )}
                    </div>
                    <p className="rounded-lg border border-hairline bg-white px-4 py-3 text-sm text-pretty text-dark/80" data-colab-nota-fise>
                        Nu trebuie să scrii clasele: echipa ECOTIC numără fișele din pozele trimise.
                    </p>
                </>
            )}

            <label className="flex flex-col gap-1">
                <span className="font-medium">Descriere (opțional)</span>
                <Textarea value={description} maxLength={2000} rows={4} className="text-base"
                          placeholder={cereClase ? 'Ce ați făcut, ce ați colectat…' : 'Cum a decurs lecția…'}
                          onChange={(e) => onChange({ ...valori, description: e.target.value })} />
            </label>
            <Button type="submit" className="min-h-12 w-full" disabled={!canContinue}>Continuă</Button>
        </form>
    );
}
